import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Box, Button, Column } from '@tempmee/react';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { FormActions } from '~/components/shared/forms';
import { SectionCard, ShiftRequestHeader } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { useRecentProfessionals } from '~/domain/RecentProfessionals';
import { countInviteCategories } from '~/commonComponents/InviteProfessional/inviteCategories';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { fireInviteSent, fireShiftRequestFillPreferencesSubmitted } from '~/posthogs/events';
import { ShiftRequestFlowStep } from '~/posthogs/events/ShiftRequest/flowSteps';
import { useShiftRequestFlowTracking } from '../ShiftRequestFlowTracking';
import { useIsFormValid } from '../hooks/useIsFormValid';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { SpeedUpCoverageSection } from './SpeedUpCoverageSection';

export const AddInvitesScreen = () => {
  const { control, watch } = useFormContext<IShiftDetailsFormValues>();
  const navigate = useNavigate();
  const isFormValid = useIsFormValid();
  const dates = watch('dates');
  const officeId = watch('officeId');
  // Sourced from React Query (single source of truth) — used only to bucket the
  // invite_sent analytics event by professional category.
  const { data: favoriteProfessionals = [] } = useFavoriteProfessionals({
    officeId,
    enabled: Boolean(officeId),
  });
  const { data: recentProfessionals = [] } = useRecentProfessionals({
    officeId,
    enabled: Boolean(officeId),
  });

  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { recordStep } = useShiftRequestFlowTracking();

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  useEffect(() => {
    if (dates.length === 0) {
      allowNavigation();
      navigate('/dashboard/shift-request/select-dates', { replace: true });
      return;
    }
    if (!isFormValid) {
      allowNavigation();
      navigate('/dashboard/multi-day/info', { replace: true });
    }
  }, [dates.length, isFormValid, allowNavigation, navigate]);

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/info');
  }, [allowNavigation, navigate]);

  const handleReviewShifts = useCallback(() => {
    // Fire invite_sent for the separated invite step, but only when at least one
    // professional is selected (no event for an empty invite list). Break the
    // count down by professional category, mirroring the mobile invite step.
    const invitedProfessionalIds = watch('invite') ?? [];
    if (invitedProfessionalIds.length > 0) {
      const counts = countInviteCategories(
        invitedProfessionalIds,
        favoriteProfessionals,
        recentProfessionals,
      );
      fireInviteSent({
        source: 'Shift Request',
        totalInvitesSent: counts.total,
        favoriteInvitesSent: counts.favorite,
        workedWithInvitesSent: counts.workedWith,
        newToYouInvitesSent: counts.newToYou,
      });
    }

    fireShiftRequestFillPreferencesSubmitted();
    recordStep(ShiftRequestFlowStep.FillPreferencesSubmitted);
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [watch, favoriteProfessionals, recentProfessionals, allowNavigation, navigate, recordStep]);

  return (
    <ShiftRequestHeader currentStep={2} onBack={handleBack} maxWidth={1120}>
      <Column align="center" style={{ gap: 24, width: 730, alignSelf: 'center' }}>
        <SectionCard title="Speed Up Your Coverage" testId="speed-up-card">
          <SpeedUpCoverageSection control={control} watch={watch} />
        </SectionCard>
      </Column>

      <FormActions>
        <Button
          data-testid="review-shifts-button"
          data-cy="review-shifts-button"
          onClick={handleReviewShifts}
          size="lg"
        >
          Review Shifts
        </Button>
      </FormActions>

      {showNavigationGuard && (
        <ConfirmPopup
          Icon={() => (
            <Box style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.warning[500]} />
            </Box>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </ShiftRequestHeader>
  );
};
